import { useState, useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { Wifi, WifiOff } from 'lucide-react';
import api from '../utils/api';

const PRINTERS = ['H2D', 'P1S', 'A1'];
const NOZZLES = ['0.4mm', '0.6mm'];

function mkWsUrl(productId, type) {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
  const host = isLocal ? `${window.location.hostname}:3800` : window.location.host;
  return { url: `${proto}//${host}/yjs`, room: `product-${type}-${productId}` };
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return '방금';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
}

export default function ProductPrintSettings({ product, onUpdate }) {
  const productId = product.id;
  const productRef = useRef(product);
  useEffect(() => { productRef.current = product; });

  const [fields, setFields] = useState({
    worker: '', phone: '', printer: 'H2D',
    nozzle: '0.4mm', bedPlate: '', material: '', extraSettings: '',
  });
  const [admins, setAdmins] = useState([]);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);

  const ymapRef = useRef(null);
  const adminsRef = useRef([]);
  const initDone = useRef(false);
  const saveTimer = useRef(null);
  const saveStatusTimer = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; });
  useEffect(() => { adminsRef.current = admins; }, [admins]);

  useEffect(() => {
    api.get('/admins/dropdown').then(r => setAdmins(r.data || [])).catch(() => {});
  }, []);

  // 핵심: provider를 useEffect 내부에서 생성 → React Strict Mode 호환
  useEffect(() => {
    initDone.current = false;
    const doc = new Y.Doc();
    const ymap = doc.getMap('data');
    ymapRef.current = ymap;

    const token = localStorage.getItem('mfg_token');
    const { url, room } = mkWsUrl(productId, 'settings');
    const prov = new WebsocketProvider(url, room, doc, { params: { token } });

    prov.on('status', ({ status }) => setConnected(status === 'connected'));
    if (prov.wsconnected) setConnected(true);

    const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
    prov.awareness.setLocalStateField('user', { name: user.name || user.username || '?', color: '#00B2FF' });

    const awarenessHandler = () => {
      const states = [];
      prov.awareness.getStates().forEach((state, clientId) => {
        if (clientId !== doc.clientID && state.user) states.push(state.user);
      });
      setPeers(states);
    };
    prov.awareness.on('change', awarenessHandler);

    const tryInit = () => {
      if (initDone.current) return;
      initDone.current = true;
      if (ymap.size === 0) {
        const p = productRef.current;
        const saved = p.printSettings || {};
        // 기존 printSettingsMemo가 있으면 extraSettings 초기값으로 사용
        const extraSettings = (saved.extraSettings != null && saved.extraSettings !== '')
          ? saved.extraSettings
          : (p.printSettingsMemo || '');
        const initial = {
          worker: '', phone: '', printer: 'H2D', nozzle: '0.4mm',
          bedPlate: '', material: '',
          ...saved,
          extraSettings,
        };
        doc.transact(() => {
          Object.entries(initial).forEach(([k, v]) => ymap.set(k, String(v ?? '')));
        });
      }
    };

    const onSync = (isSynced) => { if (isSynced) setTimeout(tryInit, 100); };
    prov.on('synced', onSync);
    if (prov.synced) setTimeout(tryInit, 100);

    const ymapObserver = () => {
      const keys = ['worker', 'phone', 'printer', 'nozzle', 'bedPlate', 'material', 'extraSettings'];
      const next = {};
      keys.forEach(k => {
        const v = ymap.get(k);
        if (v !== undefined) next[k] = v;
      });
      if (Object.keys(next).length > 0) setFields(prev => ({ ...prev, ...next }));
    };
    ymap.observe(ymapObserver);

    return () => {
      setConnected(false);
      clearTimeout(saveTimer.current);
      clearTimeout(saveStatusTimer.current);
      prov.awareness.off('change', awarenessHandler);
      prov.off('synced', onSync);
      ymap.unobserve(ymapObserver);
      prov.destroy();
      doc.destroy();
    };
  }, [productId]);

  const triggerSave = () => {
    clearTimeout(saveTimer.current);
    clearTimeout(saveStatusTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const ymap = ymapRef.current;
        if (!ymap) return;
        const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
        const data = {
          worker: ymap.get('worker') || '',
          phone: ymap.get('phone') || '',
          printer: ymap.get('printer') || 'H2D',
          nozzle: ymap.get('nozzle') || '0.4mm',
          bedPlate: ymap.get('bedPlate') || '',
          material: ymap.get('material') || '',
          extraSettings: ymap.get('extraSettings') || '',
          _meta: { by: user.name || user.username || '?', at: new Date().toISOString() },
        };
        const res = await api.put(`/products/${productId}`, { printSettings: data });
        onUpdateRef.current(res.data);
        setSaveStatus('saved');
        saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus('error');
      }
    }, 800);
  };

  const setField = (key, value) => {
    ymapRef.current?.set(key, value);
    if (key === 'worker') {
      const found = adminsRef.current.find(a => a.name === value);
      if (found?.phone) ymapRef.current?.set('phone', found.phone);
    }
    triggerSave();
  };

  const inputCls = 'w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600';
  const labelCls = 'text-xs text-gray-400 mb-1 block';
  const sectionCls = 'bg-gray-900 border border-gray-800 rounded-2xl p-4 flex flex-col gap-3';

  const meta = product.printSettings?._meta;

  return (
    <div className="flex flex-col gap-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {connected
            ? <Wifi size={13} className="text-green-400" />
            : <WifiOff size={13} className="text-gray-600" />}
          <span className="text-[10px] text-gray-500">
            {connected ? `실시간 협업 중${peers.length > 0 ? ` · ${peers.map(p => p.name).join(', ')}` : ''}` : '연결 중...'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {meta?.at && (
            <span className="text-[10px] text-gray-600">
              {meta.by} · {fmtDate(meta.at)}
            </span>
          )}
          <span className="text-[10px] text-gray-500">
            {saveStatus === 'saving' ? '저장 중...' : saveStatus === 'saved' ? '저장됨 ✓' : saveStatus === 'error' ? '저장 실패' : ''}
          </span>
        </div>
      </div>

      {/* 1. 기본정보 */}
      <div className={sectionCls}>
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">기본정보</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>작업자</label>
            <select className={inputCls} value={fields.worker} onChange={e => setField('worker', e.target.value)}>
              <option value="">선택</option>
              {admins.map(a => <option key={a.id} value={a.name}>{a.name}{a.rank ? ` (${a.rank})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>연락처</label>
            <input className={inputCls} placeholder="010-0000-0000" value={fields.phone}
              onChange={e => setField('phone', e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>적용 프린터</label>
          <div className="flex gap-2">
            {PRINTERS.map(p => (
              <button key={p} onClick={() => setField('printer', p)}
                className={`px-4 py-2 text-sm rounded-xl border transition-colors ${
                  fields.printer === p
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
                }`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 2. 소재 및 하드웨어 조건 */}
      <div className={sectionCls}>
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">소재 및 하드웨어 조건</p>
        <div>
          <label className={labelCls}>노즐 구경</label>
          <div className="flex gap-2">
            {NOZZLES.map(n => (
              <button key={n} onClick={() => setField('nozzle', n)}
                className={`px-4 py-2 text-sm rounded-xl border transition-colors ${
                  fields.nozzle === n
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
                }`}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>베드 플레이트 종류</label>
          <input className={inputCls} placeholder="예: Cool Plate, Engineering Plate..." value={fields.bedPlate}
            onChange={e => setField('bedPlate', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>소재 종류 및 브랜드</label>
          <textarea className={`${inputCls} resize-none`} rows={3} placeholder="예: PETG-CF / Bambu Lab, TPU 90A / Polymaker..."
            value={fields.material} onChange={e => setField('material', e.target.value)} />
        </div>
      </div>

      {/* 3. 추가 슬라이싱 설정 */}
      <div className={sectionCls}>
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">추가 슬라이싱 설정</p>
        <textarea className={`${inputCls} resize-none`} rows={10}
          placeholder="레이어 두께, 서포트 설정, 인필 패턴, 속도 조정 등..."
          value={fields.extraSettings} onChange={e => setField('extraSettings', e.target.value)} />
      </div>
    </div>
  );
}
